<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Header -->
    <header v-if="isAdmin" class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
      <div class="flex flex-wrap justify-between items-center gap-2 max-w-7xl mx-auto">
        <h1 class="text-xl font-semibold text-indigo-300">จัดการพ่อค้าเร่</h1>
        <div class="flex flex-wrap items-center gap-2">
          <TemplateExportButton
            collection-name="gameMerchants"
            file-name="gameMerchants.json"
            label="Export gameMerchants.json"
          />
          <button 
            @click="importLatestMerchants" 
            :disabled="isImporting"
            class="bg-emerald-600 hover:bg-emerald-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="Import พ่อค้า 15 คนล่าสุด"
          >
            <i class="fas fa-file-import text-xs"></i> 
            {{ isImporting ? 'กำลัง Import...' : 'Import พ่อค้า 15 คน' }}
          </button>
          <span v-if="importMessage" class="text-xs text-emerald-300">{{ importMessage }}</span>
        <button 
          @click="toggleModal = 'AddMerchantModal'" 
          class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label="เพิ่มพ่อค้าเร่ใหม่"
        >
          <i class="fas fa-plus-circle text-xs"></i> เพิ่มพ่อค้าเร่ใหม่
        </button>
        </div>
      </div>
    </header>

    <!-- Tabs -->
    <div  class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
      <div class="flex space-x-4">
        <button 
          @click="setActiveTab('filter')" 
          :class="{'bg-indigo-600': activeTab === 'filter', 'bg-gray-700': activeTab !== 'filter'}" 
          class="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors duration-200"
        >
          ตัวกรองพ่อค้า
        </button>
        <button 
          @click="setActiveTab('recent')" 
          :class="{'bg-indigo-600': activeTab === 'recent', 'bg-gray-700': activeTab !== 'recent'}" 
          class="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors duration-200"
        >
          พ่อค้าเพิ่มล่าสุด
        </button>
      </div>
    </div>

    <!-- Filters Tab -->
    <section v-if="activeTab === 'filter'" class="w-full">
      <div class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
        <h3 class="text-sm font-semibold text-indigo-200 mb-3">ตัวกรองพ่อค้าเร่</h3>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <!-- Location (Required) -->
          <div>
            <label class="block text-sm font-medium text-indigo-200 mb-1">เลือกสถานที่</label>
            <select 
              v-model="filterLocation" 
              @change="applyFilters" 
              class="w-full px-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              aria-label="เลือกสถานที่พ่อค้าเร่"
            >
              <option value="">เลือกสถานที่</option>
              <option v-for="loc in locations" :key="loc" :value="loc">{{ loc }}</option>
            </select>
          </div>

          <!-- Sort -->
          <div>
            <label class="block text-sm font-medium text-indigo-200 mb-1">จัดเรียง</label>
            <select 
              v-model="sortBy" 
              @change="applyFilters" 
              class="w-full px-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              aria-label="จัดเรียงพ่อค้าเร่"
            >
              <option value="created-desc">เพิ่มล่าสุด → เก่า</option>
              <option value="created-asc">เพิ่มเก่า → ล่าสุด</option>
              <option value="name-asc">ชื่อ ก-ฮ</option>
              <option value="name-desc">ชื่อ ฮ-ก</option>
            </select>
          </div>

          <!-- Name Search -->
          <div class="relative">
            <label class="block text-sm font-medium text-indigo-200 mb-1">ค้นหาชื่อพ่อค้าเร่</label>
            <i class="fas fa-magnifying-glass absolute left-3 top-10 text-indigo-400"></i>
            <input 
              v-model="filterName" 
              @input="debouncedApplyFilters" 
              type="text" 
              placeholder="ค้นหาชื่อพ่อค้าเร่..." 
              class="w-full pl-10 pr-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              aria-label="ค้นหาชื่อพ่อค้าเร่"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Recent Merchants Tab -->
    <section v-if="activeTab === 'recent'" class="w-full">
      <div class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
        <h3 class="text-sm font-semibold text-indigo-200 mb-3">พ่อค้าเร่ที่เพิ่มล่าสุด</h3>
        <div v-if="recentMerchants.length === 0 && !isRecentLoading" class="text-center text-indigo-200 py-6">
          <i class="fas fa-shopping-cart text-2xl mb-2"></i>
          <p class="text-sm">ไม่พบพ่อค้าเร่ที่เพิ่มล่าสุด</p>
        </div>
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div 
            v-for="merchant in displayedRecentMerchants" 
            :key="merchant.id" 
            @click="openDetailsModal(merchant)"
            class="bg-gray-900 rounded-md p-4 border border-gray-800 bg-opacity-80 hover:bg-opacity-80 transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer shadow-xl"
          >
            <!-- Header -->
            <div class="flex items-center space-x-3 mb-3">
              <img 
                :src="merchant.img || 'default-merchant.png'" 
                alt="Merchant Image" 
                class="w-16 h-16 object-cover rounded-md border-2 border-indigo-400"
                @error="handleImageError"
              />
              <div>
                <h3 class="text-sm font-semibold text-indigo-200">
                  {{ merchant.name }}
                </h3>
                <p class="text-xs text-gray-400">เพิ่มเมื่อ: {{ formatDate(merchant.createdAt) }}</p>
              </div>
            </div>
            <!-- Edit Button -->
            <div class="mt-3">
              <button 
              v-if="isAdmin"
                @click.stop="openEditModal(merchant)" 
                class="w-full bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-3 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="แก้ไขพ่อค้าเร่"
              >
                <i class="fas fa-edit text-xs"></i> แก้ไข
              </button>
            </div>
          </div>
        </div>
        <!-- Load More Button for Recent Merchants -->
        <div class="mt-6 flex justify-center">
          <button 
            v-if="hasMoreRecent && !isRecentLoading" 
            @click="loadMoreRecent" 
            class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="โหลดพ่อค้าเร่เพิ่มเติม"
          >
            <i class="fas fa-arrow-down text-xs"></i> โหลดเพิ่มเติม
          </button>
          <div v-if="isRecentLoading" class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-600"></div>
          </div>
          <span v-if="!hasMoreRecent && recentMerchants.length > 0" class="text-indigo-200 text-sm">ไม่มีพ่อค้าเร่เพิ่มเติมแล้ว</span>
        </div>
      </div>
    </section>

    <!-- Main Content for Filter Tab -->
    <main v-if="activeTab === 'filter'" class="max-w-7xl mx-auto">
      <!-- Error Message -->
      <div v-if="error" class="text-red-300 text-sm text-center mb-4">
        {{ error }}
      </div>

      <!-- Prompt to Select Location -->
      <div v-if="!filterLocation" class="text-center text-indigo-200 py-6">
        <i class="fas fa-shopping-cart text-2xl mb-2"></i>
        <p class="text-sm">กรุณาเลือกสถานที่เพื่อแสดงรายการ</p>
      </div>

      <div v-else>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div 
            v-for="merchant in displayedMerchants" 
            :key="merchant.id" 
            @click="openDetailsModal(merchant)"
            class="bg-gray-900 rounded-md p-4 border border-gray-800 bg-opacity-80 hover:bg-opacity-80 transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer shadow-xl"
          >
            <!-- Header -->
            <div class="flex items-center space-x-3 mb-3">
              <img 
                :src="merchant.img || 'default-merchant.png'" 
                alt="Merchant Image" 
                class="w-16 h-16 object-cover rounded-md border-2 border-indigo-400"
                @error="handleImageError"
              />
              <div>
                <h3 
                  class="text-sm font-semibold text-indigo-200"
                >
                  {{ merchant.name }} ({{ (merchant.chance / 100).toFixed(2) }}%)
                </h3>
                <p v-if="merchant.allowRob" class="text-xs text-red-400 flex items-center gap-1">
                  <i class="fas fa-skull-crossbones text-red-400"></i> สามารถปล้นได้
                </p>
              </div>
            </div>

            <!-- Stats -->
            <div v-if="merchant.stats" class="border-t border-gray-600 pt-3 mb-3">
              <h4 class="text-xs font-medium text-gray-300 mb-1">ค่าสถานะ</h4>
              <div class="grid grid-cols-3 gap-1 text-xs text-gray-300">
                <div class="flex items-center space-x-1">
                  <i class="fas fa-heart text-red-500"></i>
                  <span>{{ merchant.stats.stamina }}</span>
                </div>
                <div class="flex items-center space-x-1">
                  <i class="fas fa-dumbbell text-yellow-500"></i>
                  <span>{{ merchant.stats.str }}</span>
                </div>
                <div class="flex items-center space-x-1">
                  <i class="fas fa-brain text-blue-500"></i>
                  <span>{{ merchant.stats.int }}</span>
                </div>
                <div class="flex items-center space-x-1">
                  <i class="fas fa-running text-green-500"></i>
                  <span>{{ merchant.stats.agi }}</span>
                </div>
                <div class="flex items-center space-x-1">
                  <i class="fas fa-clover text-green-300"></i>
                  <span>{{ merchant.stats.luck }}</span>
                </div>
                <div class="flex items-center space-x-1">
                  <i class="fas fa-shield-alt text-purple-500"></i>
                  <span>{{ merchant.stats.defense }}</span>
                </div>
              </div>
            </div>
            <!-- Edit Button -->
            <div v-if="isAdmin" class="mt-3">
              <button 
                @click.stop="openEditModal(merchant)" 
                class="w-full bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-3 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="แก้ไขพ่อค้าเร่"
              >
                <i class="fas fa-edit text-xs"></i> แก้ไข
              </button>
            </div>
          </div>
        </div>

        <!-- No Merchants Message -->
        <div v-if="displayedMerchants.length === 0 && !isLoading && filterLocation" class="text-center text-indigo-200 py-6">
          <i class="fas fa-shopping-cart text-2xl mb-2"></i>
          <p class="text-sm">ไม่พบพ่อค้าเร่ที่ตรงกับการค้นหา</p>
        </div>

        <!-- Load More Button -->
        <div class="mt-6 flex justify-center">
          <button 
            v-if="hasMore && !isLoading" 
            @click="loadMore" 
            class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="โหลดเพิ่มเติม"
          >
            <i class="fas fa-arrow-down text-xs"></i> โหลดเพิ่มเติม
          </button>
          <div v-if="isLoading" class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-600"></div>
          </div>
          <span v-if="!hasMore && displayedMerchants.length > 0" class="text-indigo-200 text-sm">ไม่มีพ่อค้าเร่เพิ่มเติมแล้ว</span>
        </div>
      </div>
    </main>

    <!-- Modals -->
    <AddMerchantModal 
      v-if="toggleModal === 'AddMerchantModal'" 
      @cancelModal="toggleModal = ''" 
      @merchantAdded="resetAndFetch" 
    />
    <EditMerchantModal 
      v-if="toggleModal === 'EditMerchantModal'" 
      :merchant-id="selectedMerchantId" 
      @cancelModal="toggleModal = ''" 
      @merchantUpdated="resetAndFetch" 
    />
    <DetailMerchantModal 
      v-if="toggleModal === 'DetailMerchantModal'" 
      :merchant-id="selectedMerchantId" 
      @cancelModal="toggleModal = ''" 
      @editMerchant="openEditModalFromDetail"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
import AddMerchantModal from '../components/admin/AddMerchantModal.vue';
import EditMerchantModal from '../components/admin/EditMerchantModal.vue';
import DetailMerchantModal from '../components/admin/DetailMerchantModal.vue';
import TemplateExportButton from '../components/admin/TemplateExportButton.vue';
import gameMerchantsData from '@/data/gameMerchants.json';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';

export default {
  components: { AddMerchantModal, EditMerchantModal, DetailMerchantModal, TemplateExportButton },
  setup() {
    const toggleModal = ref('');
    const merchants = ref([]);
    const recentMerchants = ref([]);
    const filterName = ref('');
    const filterLocation = ref('');
    const selectedMerchantId = ref(null);
    const isLoading = ref(false);
    const isRecentLoading = ref(false);
    const isImporting = ref(false);
    const importMessage = ref('');
    const isAdmin = ref(false);
    const error = ref('');
    const merchantsPerLoad = 60;
    const currentPage = ref(1);
    const recentPage = ref(1);
    const maps = ref([]);
    const activeTab = ref('filter');
    const sortBy = ref('created-desc');

    // Define the custom order for locations (maps)
    const customMapOrder = [
      'ตรอกซอย', 'ตลาด', 'สนามกอล์ฟ', 'โรงเรียน', 'มหาวิทยาลัย', 'โกดัง', 'รถไฟฟ้าใต้ดิน',
      'โรงพยาบาล', 'สยามสแควร์', 'หมู่บ้านคนรวย', 'สวนสนุก', 'โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า',
      'สถานีตำรวจ', 'หอสมุดแห่งชาติ', 'สถานีดับเพลิงร้าง', 'ค่ายทหาร','สถาบันวิจัยหุ่นยนต์','สถาบันวิจัยไวรัส', 'พระบรมมหาราชวัง', 'ป่าช้า', 'สุสานนักรบโบราณ'
    ];

    const locations = computed(() => {
      return maps.value
        .map(m => m.name)
        .filter(name => typeof name === 'string')
        .sort((a, b) => {
          const indexA = customMapOrder.indexOf(a);
          const indexB = customMapOrder.indexOf(b);
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return a.localeCompare(b);
        });
    });

    const { user } = getUser();

    // Debounce function for name search
    const debounce = (func, wait) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
      };
    };

    // Handle image load errors
    const handleImageError = (event) => {
      event.target.src = 'default-merchant.png';
    };

    // Format date for display
    const formatDate = (timestamp) => {
      if (!timestamp) return 'ไม่ระบุ';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const playerStore = usePlayerStore();

    watch(() => playerStore.userData, (data) => {
      if (data) {
        isAdmin.value = data.admin === true;
      } else {
        isAdmin.value = false;
      }
    }, { immediate: true });

    // Fetch maps to get locations
    const fetchMaps = async () => {
      try {
        error.value = null;
        let mapsQuery = projectFirestore.collection('maps');
        if (!isAdmin.value) {
          mapsQuery = mapsQuery.where('isActive', '==', true);
        }
        const mapsSnapshot = await mapsQuery.get();

        maps.value = mapsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: typeof data.name === 'string' ? data.name : 'Unknown',
            isActive: data.isActive !== undefined ? data.isActive : true,
            ...data
          };
        });
      } catch (err) {
        error.value = 'ไม่สามารถดึงข้อมูลแผนที่ได้';
        console.error('Error fetching maps:', err);
      }
    };

    const getTime = (createdAt) => {
      if (!createdAt) return 0;
      if (createdAt.toDate) return createdAt.toDate().getTime();
      if (createdAt.seconds) return createdAt.seconds * 1000;
      return new Date(createdAt).getTime();
    };

    // Fetch merchants from JSON with filters
    const fetchMerchants = async () => {
      try {
        isLoading.value = true;
        error.value = '';

        let newMerchants = [];

        if (filterLocation.value) {
          const mapQuery = projectFirestore.collection('maps')
            .where('name', '==', filterLocation.value)
            .limit(1);
          const mapSnapshot = await mapQuery.get();

          if (!mapSnapshot.empty) {
            const mapData = mapSnapshot.docs[0].data();
            const mapMerchants = mapData.merchants || [];
            newMerchants = mapMerchants.map(m => {
              const data = gameMerchantsData[m.name];
              if (!data) return null;
              return { id: m.name, ...data, chance: m.chance };
            }).filter(m => m !== null);
          }
        } else {
          newMerchants = Object.entries(gameMerchantsData).map(([id, data]) => ({
            id,
            ...data
          }));
        }

        if (filterName.value) {
          newMerchants = newMerchants.filter(merchant =>
            merchant.name.toLowerCase().includes(filterName.value.toLowerCase())
          );
        }

        // Sort
        switch (sortBy.value) {
          case 'created-desc':
            newMerchants.sort((a, b) => getTime(b.createdAt) - getTime(a.createdAt));
            break;
          case 'created-asc':
            newMerchants.sort((a, b) => getTime(a.createdAt) - getTime(b.createdAt));
            break;
          case 'name-asc':
            newMerchants.sort((a, b) => a.name.localeCompare(b.name, 'th'));
            break;
          case 'name-desc':
            newMerchants.sort((a, b) => b.name.localeCompare(a.name, 'th'));
            break;
          default:
            newMerchants.sort((a, b) => getTime(b.createdAt) - getTime(a.createdAt));
        }

        merchants.value = newMerchants;
        currentPage.value = 1;
      } catch (error) {
        console.error('Error fetching merchants:', error);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลพ่อค้าเร่';
      } finally {
        isLoading.value = false;
      }
    };

    // Fetch recent merchants from JSON
    const fetchRecentMerchants = async () => {
      try {
        isRecentLoading.value = true;
        error.value = '';

        recentMerchants.value = Object.entries(gameMerchantsData)
          .map(([id, data]) => ({
            id,
            ...data,
            createdAt: data.createdAt || null
          }))
          .filter(merchant => merchant.createdAt)
          .sort((a, b) => getTime(b.createdAt) - getTime(a.createdAt));

        recentPage.value = 1;
      } catch (error) {
        console.error('Error fetching recent merchants:', error);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลพ่อค้าเร่ล่าสุด';
      } finally {
        isRecentLoading.value = false;
      }
    };

    // Import 15 latest merchants to Firestore
    const importLatestMerchants = async () => {
      const latestNames = [
        'ชาติชาย', 'มานะ', 'ประเสริฐ', 'สมชาย', 'วิชัย',
        'สุรศักดิ์', 'ประวิทย์', 'สมศักดิ์', 'นิพนธ์', 'อำนาจ',
        'บุญมี', 'ศักดิ์ชัย', 'ธนวัฒน์', 'ณัฐพล', 'สรวุฒิ'
      ];

      try {
        isImporting.value = true;
        importMessage.value = '';
        let count = 0;

        for (const name of latestNames) {
          const data = gameMerchantsData[name];
          if (!data) continue;

          const importData = { ...data };
          if (typeof importData.createdAt === 'string') {
            importData.createdAt = new Date(importData.createdAt);
          } else if (!importData.createdAt) {
            importData.createdAt = new Date();
          }

          await projectFirestore.collection('gameMerchants').doc(name).set(importData);
          count++;
        }

        importMessage.value = `Import สำเร็จ ${count} คน!`;
        setTimeout(() => { importMessage.value = ''; }, 3000);

        // Refresh displayed data
        await fetchMerchants();
        if (activeTab.value === 'recent') {
          await fetchRecentMerchants();
        }
      } catch (err) {
        console.error('Import error:', err);
        importMessage.value = 'Import ล้มเหลว!';
      } finally {
        isImporting.value = false;
      }
    };

    // Compute displayed merchants for pagination
    const displayedMerchants = computed(() => {
      const end = currentPage.value * merchantsPerLoad;
      return merchants.value.slice(0, end);
    });

    const displayedRecentMerchants = computed(() => {
      const end = recentPage.value * merchantsPerLoad;
      return recentMerchants.value.slice(0, end);
    });

    const hasMore = computed(() => {
      return (currentPage.value * merchantsPerLoad) < merchants.value.length;
    });

    const hasMoreRecent = computed(() => {
      return (recentPage.value * merchantsPerLoad) < recentMerchants.value.length;
    });

    // Apply filters and reset
    const applyFilters = () => {
      merchants.value = [];
      currentPage.value = 1;
      fetchMerchants();
    };

    // Debounced version of applyFilters for name search
    const debouncedApplyFilters = debounce(applyFilters, 300);

    // Load more merchants
    const loadMore = () => {
      currentPage.value += 1;
    };

    const loadMoreRecent = () => {
      recentPage.value += 1;
    };

    // Reset and fetch merchants
    const resetAndFetch = () => {
      merchants.value = [];
      currentPage.value = 1;
      recentMerchants.value = [];
      recentPage.value = 1;
      fetchMerchants();
      if (activeTab.value === 'recent') {
        fetchRecentMerchants();
      }
    };

    // Set active tab and fetch data if necessary
    const setActiveTab = (tab) => {
      activeTab.value = tab;
      if (tab === 'recent') {
        recentMerchants.value = [];
        recentPage.value = 1;
        fetchRecentMerchants();
      } else if (tab === 'filter') {
        merchants.value = [];
        currentPage.value = 1;
        fetchMerchants();
      }
    };

    // Open edit modal
    const openEditModal = (merchant) => {
      selectedMerchantId.value = merchant.id;
      toggleModal.value = 'EditMerchantModal';
    };

    // Open details modal
    const openDetailsModal = (merchant) => {
      selectedMerchantId.value = merchant.id;
      toggleModal.value = 'DetailMerchantModal';
    };

    // Open edit from detail
    const openEditModalFromDetail = (merchantId) => {
      selectedMerchantId.value = merchantId;
      toggleModal.value = 'EditMerchantModal';
    };

    // Fetch maps and merchants on mount
    onMounted(async () => {
      await fetchMaps();
      await fetchMerchants();
      if (isAdmin.value && activeTab.value === 'recent') {
        await fetchRecentMerchants();
      }
    });

    return { 
      toggleModal, 
      filterName, 
      filterLocation, 
      merchants, 
      recentMerchants,
      displayedMerchants, 
      displayedRecentMerchants,
      selectedMerchantId, 
      openEditModal, 
      openDetailsModal, 
      openEditModalFromDetail,
      loadMore, 
      loadMoreRecent,
      hasMore, 
      hasMoreRecent,
      isLoading, 
      isRecentLoading,
      isImporting,
      importMessage,
      isAdmin, 
      importLatestMerchants,
      resetAndFetch, 
      applyFilters, 
      debouncedApplyFilters, 
      error, 
      handleImageError,
      locations,
      activeTab,
      sortBy,
      formatDate,
      setActiveTab
    };
  },
};
</script>

<style scoped>
.min-h-screen {
  min-height: 100vh;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 5px;
}

::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

button:disabled, input:disabled, select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}
</style>
